import { StateMachine } from "@/core/arch/state_machine";
import {
  merchantSpecSchema,
  type MerchantSpec,
} from "@cpos/shared/src/dev-merchant/spec";
import { rollIdentity } from "@cpos/shared/src/dev-merchant/generators";
import {
  BUILT_IN_PRESETS,
  type BuiltInPreset,
} from "@cpos/shared/src/dev-merchant/presets";
import { DEV_MERCHANT_JOB_TYPE } from "@cpos/shared/src/dev-merchant/spec";
import type { JobRecord } from "./types";

// ── Saved presets ──────────────────────────────────────────────────────────────

export interface SavedPreset {
  id: string;
  name: string;
  description: string | null;
  /** Cognito groups applied to the rolled identity on hydration. */
  groups: string[];
  spec: Omit<MerchantSpec, "identity">;
}

// ── State ─────────────────────────────────────────────────────────────────────

export type CreateMerchantPhase =
  | { kind: "editing" }
  | { kind: "submitting" }
  | { kind: "tracking"; job: JobRecord }
  | { kind: "error"; message: string };

export interface CreateMerchantState {
  draft: MerchantSpec;
  count: number;
  savedPresets: SavedPreset[];
  phase: CreateMerchantPhase;
}

// ── Repository interface ──────────────────────────────────────────────────────

export interface CreateMerchantRepository {
  kickoff(spec: MerchantSpec, count: number): Promise<string>;
  findRunningJob(): Promise<JobRecord | null>;
  subscribeToJob(
    jobId: string,
    onUpdate: (job: JobRecord) => void,
    onError: (err: Error) => void,
  ): () => void;
  listPresets(): Promise<SavedPreset[]>;
  savePreset(
    name: string,
    description: string | undefined,
    spec: Omit<MerchantSpec, "identity">,
    groups: string[],
  ): Promise<void>;
  deletePreset(id: string): Promise<void>;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function hydrate(
  spec: Omit<MerchantSpec, "identity">,
  groups: string[],
): MerchantSpec {
  const country = spec.business.country ?? "CA";
  return {
    ...spec,
    identity: { ...rollIdentity(country), cognitoGroups: groups },
  };
}

function defaultBuiltInPreset(): BuiltInPreset {
  return (
    BUILT_IN_PRESETS.find((preset) => preset.key === "newMerchant") ??
    BUILT_IN_PRESETS[0]
  );
}

function defaultDraft(): MerchantSpec {
  const preset = defaultBuiltInPreset();
  return hydrate(preset.spec, preset.groups);
}

function stripIdentity(spec: MerchantSpec): Omit<MerchantSpec, "identity"> {
  return {
    business: spec.business,
    stripe: spec.stripe,
    entities: spec.entities,
    lifecycle: spec.lifecycle,
  };
}

// ── BLoC ─────────────────────────────────────────────────────────────────────

/**
 * Owns the create-merchant form draft and the lifecycle of the resulting
 * dev-merchant-create Job: preset loading, kickoff, subscription, and
 * terminal result display via `job.result`.
 */
export class CreateMerchantBloc extends StateMachine<CreateMerchantState> {
  private _unsubscribe: (() => void) | null = null;

  constructor(private readonly repo: CreateMerchantRepository) {
    super({
      draft: defaultDraft(),
      count: 1,
      savedPresets: [],
      phase: { kind: "editing" },
    });
    this.appendDisposeCallback(() => this._detach());
  }

  setDraft(patch: Partial<MerchantSpec>): void {
    this.setState({ ...this.state, draft: { ...this.state.draft, ...patch } });
  }

  setCount(n: number): void {
    this.setState({ ...this.state, count: n });
  }

  /** Re-rolls the identity section, preserving the currently assigned Cognito groups. */
  rollIdentitySection(): void {
    const { draft } = this.state;
    const country = draft.business.country ?? "CA";
    this.setState({
      ...this.state,
      draft: {
        ...draft,
        identity: {
          ...rollIdentity(country),
          cognitoGroups: draft.identity.cognitoGroups,
        },
      },
    });
  }

  loadBuiltInPreset(key: string): void {
    const preset = BUILT_IN_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    this.setState({
      ...this.state,
      draft: hydrate(preset.spec, preset.groups),
    });
  }

  loadSavedPreset(preset: SavedPreset): void {
    this.setState({
      ...this.state,
      draft: hydrate(preset.spec, preset.groups),
    });
  }

  async loadPresets(): Promise<void> {
    const savedPresets = await this.repo.listPresets();
    this.setState({ ...this.state, savedPresets });
  }

  async savePreset(name: string, description?: string): Promise<void> {
    const specWithoutIdentity = stripIdentity(this.state.draft);
    const groups = this.state.draft.identity.cognitoGroups;
    await this.repo.savePreset(name, description, specWithoutIdentity, groups);
    await this.loadPresets();
  }

  async deletePreset(id: string): Promise<void> {
    await this.repo.deletePreset(id);
    await this.loadPresets();
  }

  async submit(): Promise<void> {
    const parsed = merchantSpecSchema.safeParse(this.state.draft);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const path = issue.path.join(".");
      const message = path ? `${path}: ${issue.message}` : issue.message;
      this.setState({ ...this.state, phase: { kind: "error", message } });
      return;
    }

    this.setState({ ...this.state, phase: { kind: "submitting" } });
    try {
      const jobId = await this.repo.kickoff(parsed.data, this.state.count);
      const seedJob: JobRecord = {
        id: jobId,
        type: DEV_MERCHANT_JOB_TYPE,
        status: "pending",
        triggeredBy: "",
        total: this.state.count,
        processed: 0,
        counters: null,
        result: null,
        error: null,
        startedAt: null,
        completedAt: null,
      };
      this._attach(seedJob);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.setState({ ...this.state, phase: { kind: "error", message } });
    }
  }

  reset(): void {
    this._detach();
    this.setState({
      ...this.state,
      draft: defaultDraft(),
      phase: { kind: "editing" },
    });
  }

  async resumeIfRunning(): Promise<void> {
    try {
      const job = await this.repo.findRunningJob();
      if (job) this._attach(job);
    } catch {
      // Non-fatal — user sees the editing form and can kick off manually.
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private _attach(seed: JobRecord): void {
    this._detach();
    this.setState({ ...this.state, phase: { kind: "tracking", job: seed } });

    this._unsubscribe = this.repo.subscribeToJob(
      seed.id,
      (job) => {
        this.setState({ ...this.state, phase: { kind: "tracking", job } });
      },
      (err) => {
        this.setState({
          ...this.state,
          phase: { kind: "error", message: err.message },
        });
      },
    );
  }

  private _detach(): void {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }
}
